// ---------------------------------------------------------------------------
// Browser Targeting — data-layer client.
//
// Thin typed wrapper over `apiClient` for the
// `/api/v1/campaign-browser-targeting` surface (feature #70). Mirrors the
// contract declared in `apps/api/src/routes/campaign-browser-targeting.ts`.
//
// Keep this file dependency-free beyond the root `apiClient` — no React,
// no TanStack — so it is reusable from server-side smoke harnesses.
// ---------------------------------------------------------------------------

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Canonical browser codes — aligned with API BROWSER_TYPES:
//   chrome | firefox | safari | edge | opera | samsung | other
// ---------------------------------------------------------------------------

export const BROWSER_CODES = [
	"chrome",
	"firefox",
	"safari",
	"edge",
	"opera",
	"samsung",
	"other",
] as const;
export type BrowserCode = (typeof BROWSER_CODES)[number];

export const BROWSER_LABELS: Record<BrowserCode, string> = {
	chrome: "Chrome",
	firefox: "Firefox",
	safari: "Safari",
	edge: "Edge",
	opera: "Opera",
	samsung: "Samsung Internet",
	other: "Other",
};

export type TargetingMode = "allow" | "deny";

// ---------------------------------------------------------------------------
// API row + response shapes — the backend returns the browsers array sorted
// and de-duplicated; the dash simply round-trips it verbatim.
// ---------------------------------------------------------------------------

export interface BrowserTargetingRow {
	id: string;
	tenant_id: string;
	campaign_id: string;
	browsers: BrowserCode[];
	created_at: number;
	updated_at: number;
}

export interface BrowserTargetingResponse {
	targeting: BrowserTargetingRow;
}

// ---------------------------------------------------------------------------
// Deny-mode simulation — the backend only stores an allowlist. To honour the
// operator-facing "deny" semantic, the dash derives the effective allow-list
// by subtracting the chosen browsers from the full canonical set before
// sending it to the API. Reverse-mapping on read guesses the mode from the
// stored shape: if the stored list contains exactly `TOTAL - N` entries, the
// simpler mental model is "deny these N".
// ---------------------------------------------------------------------------

export function formSelectionToApiList(
	mode: TargetingMode,
	selected: readonly BrowserCode[],
): BrowserCode[] {
	const unique = Array.from(new Set(selected)) as BrowserCode[];
	if (mode === "allow") return unique.slice().sort();
	return BROWSER_CODES.filter((code) => !unique.includes(code)).slice().sort();
}

/**
 * Reverse of `formSelectionToApiList`. When the stored list covers every
 * canonical browser we default to "allow" with a full selection — the rule
 * is effectively open, which is the honest reading.
 */
export function apiListToFormSelection(
	list: readonly BrowserCode[],
): { mode: TargetingMode; selected: BrowserCode[] } {
	const set = new Set<BrowserCode>(list);
	if (set.size === 0) {
		return { mode: "allow", selected: [] };
	}
	// Deny-mode wins when 3+ codes are missing — that is a smaller representation.
	const missing = BROWSER_CODES.filter((c) => !set.has(c));
	if (missing.length > 0 && missing.length < set.size) {
		return { mode: "deny", selected: missing };
	}
	return { mode: "allow", selected: Array.from(set) };
}

// ---------------------------------------------------------------------------
// Input validation — hand-rolled schema mirroring a zod surface. Keeps dash
// zero-dep on zod/react-hook-form while preserving the familiar `.safeParse`
// shape so the form can consume it exactly as if it were a zod schema.
// ---------------------------------------------------------------------------

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export interface BrowserTargetingFormInput {
	campaignId: string;
	mode: TargetingMode;
	selected: BrowserCode[];
}

export interface BrowserTargetingFormParsed {
	campaignId: string;
	mode: TargetingMode;
	selected: BrowserCode[];
}

const CAMPAIGN_ID_MAX = 120;
// Mirrors the API CAMPAIGN_ID_RE for early client-side feedback.
const CAMPAIGN_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

export const browserTargetingFormSchema = {
	safeParse(
		input: BrowserTargetingFormInput,
	): ParseResult<BrowserTargetingFormParsed> {
		const errors: Record<string, string> = {};

		const campaignId = input.campaignId?.trim() ?? "";
		if (campaignId.length === 0) {
			errors.campaignId = "Campaign ID is required.";
		} else if (campaignId.length > CAMPAIGN_ID_MAX) {
			errors.campaignId = `Campaign ID must be ${CAMPAIGN_ID_MAX} chars or fewer.`;
		} else if (!CAMPAIGN_ID_RE.test(campaignId)) {
			errors.campaignId =
				"Use letters, numbers, hyphen or underscore (max 64).";
		}

		if (input.mode !== "allow" && input.mode !== "deny") {
			errors.mode = "Mode must be allow or deny.";
		}

		const allowed = new Set<BrowserCode>(BROWSER_CODES);
		const selected: BrowserCode[] = [];
		const seen = new Set<BrowserCode>();
		for (const code of input.selected ?? []) {
			if (!allowed.has(code)) {
				errors.selected = "Unsupported browser in selection.";
				break;
			}
			if (!seen.has(code)) {
				seen.add(code);
				selected.push(code);
			}
		}
		if (!errors.selected && selected.length === 0) {
			errors.selected = "Pick at least one browser.";
		}
		if (
			!errors.selected &&
			input.mode === "deny" &&
			selected.length === BROWSER_CODES.length
		) {
			errors.selected = "Deny mode cannot block every browser.";
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: {
				campaignId,
				mode: input.mode,
				selected,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// Remote operations — TanStack Query consumers import these directly.
// ---------------------------------------------------------------------------

const BASE = "/campaign-browser-targeting";

/**
 * Returns the targeting row for a campaign, or `null` if none exists yet.
 * 404 from the API is normalized to `null` so the query layer can render
 * an empty form without surfacing an error state.
 */
export async function fetchBrowserTargeting(
	campaignId: string,
): Promise<BrowserTargetingRow | null> {
	try {
		const res = await apiClient.get<BrowserTargetingResponse>(
			`${BASE}/${encodeURIComponent(campaignId)}`,
		);
		return res.targeting;
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return null;
		throw err;
	}
}

export interface SaveBrowserTargetingArgs {
	campaignId: string;
	mode: TargetingMode;
	selected: BrowserCode[];
}

/**
 * Upsert a browser targeting row via POST — the API atomically
 * creates-or-replaces against `(tenant_id, campaign_id)`.
 */
export async function saveBrowserTargeting(
	args: SaveBrowserTargetingArgs,
): Promise<BrowserTargetingRow> {
	const browsers = formSelectionToApiList(args.mode, args.selected);
	const res = await apiClient.post<BrowserTargetingResponse>(BASE, {
		campaignId: args.campaignId,
		browsers,
	});
	return res.targeting;
}

/** Remove the targeting row. Silently no-ops on 404. */
export async function deleteBrowserTargeting(
	campaignId: string,
): Promise<void> {
	try {
		await apiClient.delete(`${BASE}/${encodeURIComponent(campaignId)}`);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return;
		throw err;
	}
}

export const browserTargetingQueryKeys = {
	all: ["browser-targeting"] as const,
	byCampaign: (campaignId: string) =>
		[...browserTargetingQueryKeys.all, campaignId] as const,
};
